import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { PersonTargetType, TargetType } from '@/types/TargetType.ts';
import {
  BillCatalog,
  BillLine,
  BillSection,
  BookingBill,
} from '@/features/dashboard/bookingChanges/types/BookingBill.ts';
import {
  ChangePreview,
  previewOwnParticipantDelta,
  previewRowImpact,
} from '@/features/dashboard/bookingChanges/types/ChangePreview.ts';
import {
  ChangeItemDto,
  ChangeItemType,
  isChangeItemOfType,
} from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';
import { formatMoneyList } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { EMPTY_VALUE } from '@/components/ui/EmptyValue.tsx';
import {
  bookingWideDiscountDescriptions,
  findItemsForLine,
  resolveNewLabel,
  resolveNewValue,
  sectionsWithDraftLines,
} from '@/features/dashboard/bookingChanges/utils/draftItems.ts';
import {
  addMoneyLists,
  sumMoney,
} from '@/features/dashboard/bookingChanges/utils/money.ts';

/**
 * Neutralna postać podsumowania paczki: kto, co, przed → po, o ile.
 *
 * Ta sama struktura zasila krok W1 (mapper → `ChangePackageSummaryVm`) i mock
 * warstwy prezentacyjnej strony akceptacji W3 — dzięki temu operator i klient
 * patrzą dokładnie na te same pozycje, a nie na dwie równoległe listy.
 *
 * Kwot tu nie liczymy: „o ile” przychodzi z podglądu paczki. `preview === null`
 * znaczy „kwot nie znamy” i wtedy wszystkie delty są puste — puste, a nie
 * zerowe, bo zera nie da się zmyślić, nie wiedząc, o którą walutę chodzi.
 * Delta jest listą po jednej pozycji na walutę, jak wszędzie w modelu widoku.
 */

export interface ChangeSummaryItemData {
  id: string;
  label: string;
  before: string;
  after: string;
  delta: MoneyValueDto[];
}

/** Nazwa grupy pozycji bez osoby — to samo słowo, którym dzieli je ekran edycji. */
export const BOOKING_WIDE_GROUP_LABEL = 'Cała rezerwacja';
export const BOOKING_WIDE_GROUP_HINT = 'pozycje bez przypisania do osoby';

interface ChangeSummaryGroupDataBase {
  totalDelta: MoneyValueDto[];
  items: ChangeSummaryItemData[];
}

export interface ChangeSummaryPersonGroupData
  extends ChangeSummaryGroupDataBase {
  scope: 'person';
  personId: string;
  firstName: string;
  lastName: string;
  personKind: PersonTargetType;
  /** Wiek uczestnika; `null` dla osoby zapisującej. */
  age: number | null;
}

/** Pozycje ręczne bez uczestnika — zniżka czy korekta całej rezerwacji. */
export interface ChangeSummaryBookingGroupData
  extends ChangeSummaryGroupDataBase {
  scope: 'booking';
}

export type ChangeSummaryGroupData =
  | ChangeSummaryPersonGroupData
  | ChangeSummaryBookingGroupData;

/**
 * Wartość wiersza w stanie bieżącym — to, co stoi w kolumnie „przed".
 *
 * Wiersz kwotowy (cena bazowa, pozycja ręczna) nie ma wartości tekstowej, bo
 * jego wartością **jest** kwota. Bez tego podsumowanie paczki pokazywało przy
 * zmianie ceny „— → —", czyli dokładnie nic w tabeli, której jedynym zadaniem
 * jest powiedzieć „przed → po".
 */
export const lineValueText = (line: BillLine): string =>
  line.value ??
  line.chips?.join(', ') ??
  (line.amount.length > 0 ? formatMoneyList(line.amount) : null) ??
  line.amountLabel ??
  EMPTY_VALUE;

/**
 * Wartość wiersza po zmianie.
 *
 * Wiersz kwotowy nie ma nowej wartości tekstowej — jest nią przesunięta kwota,
 * więc składamy ją tak samo jak wiersz rachunku: stan z księgi plus delta
 * z podglądu, obie liczby policzone przez backend. Bez tego podsumowanie
 * pokazywało przy zmianie ceny „— → —", czyli nic.
 */
const newValueText = (
  line: BillLine,
  items: ChangeItemDto[],
  catalog: BillCatalog,
  impact: MoneyValueDto[],
): string => {
  const value = resolveNewValue(line, items, catalog);
  if (value !== null) {
    return value;
  }
  // Pozycja ręczna dopisana szkicem nie ma kwoty „przed”, więc jej kwota „po”
  // to sama delta.
  if (impact.length === 0 || (line.amount.length === 0 && !line.manual)) {
    return EMPTY_VALUE;
  }
  return formatMoneyList(addMoneyLists(line.amount, impact));
};

/**
 * Wiersze sekcji rachunku ruszane przez szkic. `participantId === null` to
 * sekcje całej rezerwacji — pozycje ręczne bez uczestnika.
 */
const sectionItems = (
  bill: BookingBill,
  sections: BillSection[],
  participantId: string | null,
  items: ChangeItemDto[],
  preview: ChangePreview | null,
): ChangeSummaryItemData[] =>
  // Razem z wierszami, które dopisuje sam szkic — pozycja ręczna nie ma
  // odpowiednika w rachunku, a w podsumowaniu musi być widoczna jak każda inna.
  sectionsWithDraftLines(sections, items, participantId).flatMap((section) =>
    section.lines.flatMap((line) => {
      // Wiersz wielowartościowy zbiera kilka pozycji paczki (po jednej na
      // wariant), a w podsumowaniu ma być jednym wierszem „przed → po”.
      const lineItems = findItemsForLine(items, line, participantId);
      if (lineItems.length === 0) {
        return [];
      }
      const impact = section.withAmounts
        ? previewRowImpact(preview, line.costLineIds, line.costKey).delta
        : [];

      return [
        {
          id: lineItems[0].id,
          label: resolveNewLabel(lineItems) ?? line.label,
          before: lineValueText(line),
          after: newValueText(line, lineItems, bill.catalog, impact),
          delta: impact,
        },
      ];
    }),
  );

/**
 * Zmienione pola zapisującego, każde jako osobny wiersz podsumowania.
 *
 * Cały zestaw kontaktowy to jedna pozycja paczki (`CLIENT_DATA`), ale czytelnik
 * podsumowania patrzy na zmiany, nie na mechanikę szkicu — e-mail i adres to
 * dla niego dwie zmiany. Stąd jedno `id` pozycji rozdziela się na wiersze.
 */
const clientItems = (
  bill: BookingBill,
  items: ChangeItemDto[],
): ChangeSummaryItemData[] => {
  const clientItem = items.find(isChangeItemOfType(ChangeItemType.CLIENT_DATA));
  if (!clientItem) {
    return [];
  }

  return bill.client.sections
    .flatMap((section) => section.lines)
    .filter((line) => line.ref?.kind === ChangeItemType.CLIENT_DATA)
    .flatMap((line) => {
      const matched = findItemsForLine(items, line, null);
      const after =
        matched.length > 0
          ? resolveNewValue(line, matched, bill.catalog)
          : null;
      if (after === null) {
        return [];
      }
      return [
        {
          // Klucz wiersza; samo `clientItem.id` powtórzyłoby się w każdym polu.
          id: `${clientItem.id}:${line.id}`,
          label: line.label,
          before: lineValueText(line),
          after,
          // Zmiana danych zapisującego nigdy nie rusza kwot.
          delta: [],
        },
      ];
    });
};

/**
 * Pozycje paczki pogrupowane: uczestnicy, zapisujący, na końcu cała rezerwacja
 * — w tej kolejności, w jakiej stoją na ekranie edycji. Grupa bez pozycji nie
 * powstaje.
 */
export const buildChangeSummaryGroups = (
  bill: BookingBill,
  items: ChangeItemDto[],
  preview: ChangePreview | null = null,
): ChangeSummaryGroupData[] => {
  const bookingWideDescriptions = bookingWideDiscountDescriptions(items);
  const groups: ChangeSummaryGroupData[] = bill.participants
    .map(
      (participant): ChangeSummaryPersonGroupData => ({
        scope: 'person',
        personId: participant.participantId,
        firstName: participant.firstName,
        lastName: participant.lastName,
        personKind: TargetType.PARTICIPANT,
        age: participant.age,
        totalDelta: previewOwnParticipantDelta(
          preview,
          participant.participantId,
          bookingWideDescriptions,
        ),
        items: sectionItems(
          bill,
          participant.sections,
          participant.participantId,
          items,
          preview,
        ),
      }),
    )
    .filter((group) => group.items.length > 0);

  const client = clientItems(bill, items);
  if (client.length > 0) {
    groups.push({
      scope: 'person',
      personId: bill.client.clientId,
      firstName: bill.client.firstName,
      lastName: bill.client.lastName,
      personKind: TargetType.CLIENT,
      age: null,
      totalDelta: [],
      items: client,
    });
  }

  const bookingWide = sectionItems(
    bill,
    bill.bookingWideSections,
    null,
    items,
    preview,
  );
  if (bookingWide.length > 0) {
    groups.push({
      scope: 'booking',
      // Z wierszy, nie z grupy `null` podglądu: zniżka rozłożona na
      // uczestników w tej grupie nie zostawia nic.
      totalDelta: sumMoney(bookingWide.flatMap((item) => item.delta)),
      items: bookingWide,
    });
  }

  return groups;
};
